import React, { useMemo, useState } from 'react';
import {
  useGetUpcomingAnniversariesQuery,
  useGetUpcomingBirthdaysQuery,
} from '../redux/employeeApi';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** Next calendar occurrence (month/day) from a reference date — for sorting upcoming birthdays / anniversaries. */
function nextAnnualOccurrenceTime(dateStr) {
  if (!dateStr) return Number.MAX_SAFE_INTEGER;
  const ref = new Date(dateStr);
  if (Number.isNaN(ref.getTime())) return Number.MAX_SAFE_INTEGER;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(now.getFullYear(), ref.getMonth(), ref.getDate());
  if (next < today) {
    next = new Date(now.getFullYear() + 1, ref.getMonth(), ref.getDate());
  }
  return next.getTime();
}

const MAX_LANDING_BIRTHDAY_ANNIVERSARY = 5;

const EVENTS_TABS = [
  { label: 'Birthday', shortLabel: 'Birthday' },
  { label: 'Work Anniversary', shortLabel: 'Anniversary' },
  { label: 'Employee Of The Month', shortLabel: 'EOTM' },
];

// Keep "Employee Of The Month" as static data (as per requirement).
const EMPLOYEE_OF_THE_MONTH_EVENTS = [
  { date: '26 Jan', text: 'Lorem Ipsum is simply dummy text of the printing' },
  { date: '25 Jan', text: 'Lorem Ipsum is simply dummy text of the printing' },
  { date: '24 Jan', text: 'Lorem Ipsum is simply dummy text of the printing' },
  { date: '23 Jan', text: 'Lorem Ipsum is simply dummy text of the printing' },
  { date: '22 Jan', text: 'Lorem Ipsum is simply dummy text of the printing' },
];

function Events() {
  const [activeEventTab, setActiveEventTab] = useState(0);

  const {
    data: birthdaysRes,
    isFetching: isFetchingBirthdays,
  } = useGetUpcomingBirthdaysQuery();

  const {
    data: anniversariesRes,
    isFetching: isFetchingAnniversaries,
  } = useGetUpcomingAnniversariesQuery();

  const birthdayEvents = useMemo(() => {
    const rows = (birthdaysRes?.data || []).map((e) => ({
      id: e.id,
      text: e.name,
      date: formatEventDate(e.dob),
      avatarUrl: e.employee_image || '',
      _order: nextAnnualOccurrenceTime(e.dob),
    }));
    rows.sort((a, b) => a._order - b._order);
    return rows
      .slice(0, MAX_LANDING_BIRTHDAY_ANNIVERSARY)
      .map(({ _order, ...event }) => event);
  }, [birthdaysRes]);

  const anniversaryEvents = useMemo(() => {
    const rows = (anniversariesRes?.data || []).map((e) => ({
      id: e.id,
      text: e.name,
      date: formatEventDate(e.date_of_joining),
      avatarUrl: e.employee_image || '',
      _order: nextAnnualOccurrenceTime(e.date_of_joining),
    }));
    rows.sort((a, b) => a._order - b._order);
    return rows
      .slice(0, MAX_LANDING_BIRTHDAY_ANNIVERSARY)
      .map(({ _order, ...event }) => event);
  }, [anniversariesRes]);

  const eventsToRender =
    activeEventTab === 0
      ? birthdayEvents
      : activeEventTab === 1
        ? anniversaryEvents
        : EMPLOYEE_OF_THE_MONTH_EVENTS;

  const isLoading =
    (activeEventTab === 0 && isFetchingBirthdays) ||
    (activeEventTab === 1 && isFetchingAnniversaries);

  const EVENTS = isLoading
    ? [{ id: 'loading', text: 'Loading...', date: '' }]
    : eventsToRender;

  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="-mx-1 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-1 pb-1">
          {EVENTS_TABS.map((tab, i) => (
            <button
              key={tab.label}
              type="button"
              className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                i === activeEventTab
                  ? 'bg-brand text-white'
                  : 'bg-[#F7E5D5] text-gray-700'
              }`}
              onClick={() => setActiveEventTab(i)}
            >
              <span className="sm:hidden">{tab.shortLabel}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#F7E5D5] text-sm text-gray-600"
            aria-label="Previous"
          >
            &lt;
          </button>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#F7E5D5] text-sm text-gray-600"
            aria-label="Next"
          >
            &gt;
          </button>
        </div>
      </div>

      <ul key={activeEventTab} className="divide-y divide-gray-200">
        {EVENTS.map((event) => (
          <li key={event.id || event.text} className="flex items-start gap-3 py-3">
            {event.avatarUrl ? (
              <img
                src={event.avatarUrl}
                alt={event.text}
                className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="h-12 w-12 flex-shrink-0 rounded-full bg-gradient-to-br from-gray-200 to-gray-100" />
            )}
            <div className="flex-1">
              <p className="mb-1 text-sm text-gray-700">{event.text}</p>
              <span className="inline-block rounded-full bg-[#F7E5D5] px-3 py-1 text-xs font-semibold text-gray-900">
                {event.date}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Events;
